import { FC } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { BackIcon, FormCloseIcon, GreenFilledTickIcon, GreyStrokeInfoIcon } from 'assets/icons';
import Button, { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import useFSMPlanInfo from '../hooks/useFSMPlanInfo';

type ConfirmUpgradeToFSMPlusProps = {
  handleClose: VoidFn;
  onStartTrial: VoidFn;
  isLoading?: boolean;
  onBack: VoidFn;
};

const ConfirmUpgradeToFSMPlus: FC<ConfirmUpgradeToFSMPlusProps> = ({
  handleClose,
  onStartTrial,
  isLoading = false,
  onBack
}) => {
  const { fsmPlusPlan, isTrialEnded, isFSMLitePlan } = useFSMPlanInfo();

  const trialDuration = fsmPlusPlan?.trialDuration ?? 14;
  const subscriptionPrice = fsmPlusPlan?.formattedPrice ?? '$99.99';

  const trialBenefits = [
    ...(trialDuration > 0 ? [`Free ${trialDuration}-day trial — no card needed`] : []),
    `${subscriptionPrice} / month after trial ends`
  ];

  return (
    <CustomModal open={true} closeModal={handleClose} width="sm:w-[90%] md:w-[579px]" height="lg:max-h-[95vh]">
      <div className="relative w-full rounded-xl bg-white p-6">
        <BackIcon
          onClick={onBack}
          className="path-stroke-current absolute left-6 top-4 h-6 w-6 cursor-pointer text-primaryText"
          tabIndex={0}
        />
        <FormCloseIcon
          onClick={handleClose}
          className="absolute right-3 top-3 h-6 w-6 cursor-pointer"
          tabIndex={0}
          aria-label="Close modal"
        />

        {/* Title */}
        <h2 className="mb-8 text-center text-2xl font-bold text-heading">Upgrade to Field Services Plus</h2>
        <p className="text-sbase text-primaryText">
          Start using advanced dispatching and payroll tools built for teams.
        </p>

        {isTrialEnded && (
          <div className="my-4 space-y-3">
            <div className="flex items-center gap-3 font-medium">
              <GreenFilledTickIcon className="shrink-0" />
              <p className="text-sbase text-primaryText">{`${subscriptionPrice} / month`}</p>
            </div>
          </div>
        )}

        {isTrialEnded && (
          <div className="mb-4 rounded-md bg-secondaryBtn p-3">
            <div className="flex items-center gap-2">
              <GreyStrokeInfoIcon className="shrink-0" />
              <p className="text-sbase font-medium text-primaryText">You can cancel or downgrade anytime.</p>
            </div>
          </div>
        )}

        {/* Trial benefits */}
        {!isTrialEnded && (
          <div className="my-4 space-y-3">
            {trialBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 font-medium">
                <GreenFilledTickIcon className="shrink-0" />
                <p className="text-sbase text-primaryText">{benefit}</p>
              </div>
            ))}
          </div>
        )}

        {/* Info notice */}
        {!isTrialEnded && (
          <div className="mb-4 rounded-md bg-secondaryBtn p-3">
            <div className="flex items-center gap-2">
              <GreyStrokeInfoIcon className="shrink-0" />
              <p className="text-sbase font-medium text-primaryText">
                You can cancel or downgrade anytime before your trial ends.
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-4">
          <Button
            onClick={onStartTrial}
            type={ButtonType.PRIMARY}
            className="h-12 w-full"
            isLoading={isLoading}
            id="start-trial-btn"
            aria-label={`Start free trial for ${trialDuration} days, then ${subscriptionPrice} / month`}>
            {isTrialEnded || trialDuration === 0
              ? 'Upgrade to Field Services Plus'
              : `Start free trial for ${trialDuration} days, then ${subscriptionPrice} / month`}
          </Button>

          <Button
            onClick={handleClose}
            type={ButtonType.SECONDARY}
            className="w-full"
            id="cancel-upgrade-btn"
            aria-label="Cancel upgrade">
            Cancel
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ConfirmUpgradeToFSMPlus;
